import type { FC } from 'react';
import { useCallback, useState, useEffect } from 'react';
import type {
    UseFormClearErrors,
    UseFormGetValues,
    UseFormSetError,
    UseFormSetValue,
} from 'react-hook-form';

import { FormInput, FormSelect, HttpStatus } from '@catalyze/ui';
import type { TFormInputs } from 'features/Profile/pages/Profile';
import { useOptionsWithSearch } from 'features/SignUp/hooks/useOptionsWithSearch';

import { FormLine } from '../FormLine';

interface IBrokerageDetails {
    zip: string | undefined;
    formMethods: {
        getValues: UseFormGetValues<TFormInputs>;
        setValue: UseFormSetValue<TFormInputs>;
        clearErrors: UseFormClearErrors<TFormInputs>;
        setError: UseFormSetError<TFormInputs>;
    };
}

export const BrokerageDetails: FC<IBrokerageDetails> = ({
    zip,
    formMethods,
}) => {
    const [searchValue, setSearchValue] = useState('');
    const { getValues, setError, clearErrors, setValue } = formMethods;

    // Limits characters to numbers of max length
    const setValidSearchValue = useCallback(
        (test: string) =>
            setSearchValue(test.replace(/[^0-9]/u, '').slice(0, 5)),
        []
    );

    const { options, isLoading, error, search, foundSearchOption } =
        useOptionsWithSearch({
            search: searchValue,
            prefetchValue: getValues()?.zip || undefined,
        });

    // Sets newly found option as value, reset errors
    useEffect(() => {
        if (foundSearchOption) {
            setValue('zip', foundSearchOption.value);
        }
    }, [foundSearchOption, setValue]);

    // Sets error if zip is not found
    useEffect(() => {
        if (error?.response) {
            setError('zip', {
                message:
                    Number(error?.response.status || 0) === HttpStatus.NOT_FOUND
                        ? 'Zip code not found'
                        : 'Error occured, please try again',
            });
            setValue('zip', '');
        } else {
            clearErrors('zip');
        }
    }, [error, setError, clearErrors, setValue]);

    return (
        <>
            <FormLine>
                <FormInput
                    type="number"
                    id="brokerageLicenseNumber"
                    label="License number"
                />
                <FormInput id="brokerageName" label="Brokerage name" />
            </FormLine>
            <FormSelect
                value={zip}
                id="zip"
                label="Your location"
                options={options}
                isCreatable
                selectProps={{
                    inputValue: searchValue,
                    onInputChange: setValidSearchValue,
                    onCreateOption: (value) => {
                        search({ code: value });
                    },
                    formatCreateLabel: (value) => `Search for ${value}`,
                    isLoading,
                    isDisabled: isLoading,
                }}
            />
        </>
    );
};
